/**
 * app/screens/AddRoomScreen.tsx
 * ------------------------------
 * What: Form for creating a new room.
 * Why: Lets users organise items by location.
 */
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput as BaseTextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  SafeAreaView,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, CommonActions } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import type { RootStackParamList } from '../types/navigation';
import { getPlatform } from '../utils/platform';
import { usePaginatedRooms } from '../../lib/DataContext';
import { Ionicons } from '@expo/vector-icons';
import ThemedButton from '../components/ThemedButton';
import { useTheme } from '../../lib/ThemeContext';
import FormTextInput from '../components/FormTextInput';
import FormLabel from '../components/FormLabel';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

type AddRoomScreenProps = {
  navigatorContext?: boolean;
};

const TextInput = FormTextInput as unknown as typeof BaseTextInput;

export default function AddRoomScreen(props: AddRoomScreenProps) {
  const navigation = useNavigation<NavigationProp>();
  const { tier } = useSubscription();
  const { refreshCounts } = useSubscription();
  const { user } = useAuth();
  const { refresh } = usePaginatedRooms();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { theme, themeType } = useTheme();

  const handleAddRoom = async () => {
    if (!user) {
      setError('You must be logged in to add a room');
      return;
    }

    if (!name.trim()) {
      setError('Room name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error: dbError } = await supabase
        .from('rooms')
        .insert([
          {
            name: name.trim(),
            description: description.trim() || null,
            user_id: user.id,
            created_with: getPlatform(),
            updated_with: getPlatform(),
          }
        ])
        .select()
        .single();

      if (dbError) {
        console.error('Error adding room:', dbError);
        setError(dbError.message || 'Failed to add room. Please try again.');
        return;
      }

      // Refresh the subscription counts before navigating
      await refreshCounts();
      // Refresh the room list instantly
      await refresh();
      // Navigate back to rooms list
      navigation.dispatch(
        CommonActions.navigate({
          name: 'RoomsTab',
          params: { refresh: true, showSuccess: true, message: 'Room added successfully!' }
        })
      );
    } catch (error: any) {
      console.error('Error adding room:', error);
      setError(error.message || 'Failed to add room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Replace deprecated shadow* props with boxShadow for web
  const shadowStyle = Platform.OS === 'web' 
    ? { boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' } 
    : { 
        shadowColor: '#000', 
        shadowOpacity: 0.25, 
        shadowRadius: 3.84, 
        shadowOffset: { width: 0, height: 2 } 
      };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle="light-content" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[styles.container, { backgroundColor: theme.background }]}
      >
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          {/* Modern Header */}
          <View style={{ paddingTop: 32, paddingBottom: 16, paddingHorizontal: 20, alignItems: 'center', backgroundColor: theme.background }}>
            <View style={{ position: 'absolute', left: 20, top: 32 }}>
              <TouchableOpacity
                style={{ padding: 8, borderRadius: 20, backgroundColor: theme.inputBackground }}
                onPress={() => navigation.goBack()}
                accessibilityLabel="Back"
                accessibilityRole="button"
              >
                <Ionicons name="arrow-back" size={26} color={theme.text} />
              </TouchableOpacity>
            </View>
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 4 }}>Add Room</Text>
            <View style={{ height: 2, backgroundColor: theme.border, width: 60, borderRadius: 2, marginTop: 8 }} />
          </View>

          {/* Modern Card Form */}
          <View style={{ padding: 20, alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.card, borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, ...shadowStyle, ...(themeType === 'blueNeon' ? (Platform.OS === 'web' ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any : { shadowColor: theme.accent, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 6, elevation: 6 }) : {}) }}>
              {/* Error Message */}
              {error && (
                <View style={{ backgroundColor: '#ffeaea', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '600', fontSize: 15 }}>{error}</Text>
                </View>
              )}
              {/* Room Name */}
              <FormLabel>Room Name *</FormLabel>
              <TextInput
                style={{
                  backgroundColor: theme.inputBackground,
                  color: theme.text,
                  borderColor: theme.border,
                  borderWidth: 1,
                  borderRadius: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  fontSize: 16,
                }}
                value={name}
                onChangeText={setName}
                placeholder="Enter room name"
                placeholderTextColor={theme.secondaryText}
                autoCapitalize="words"
                maxLength={25}
                returnKeyType="next"
              />
              <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{name.length}/25 characters</Text>

              {/* Description */}
              <FormLabel>Description</FormLabel>
              <TextInput
                style={{
                  backgroundColor: theme.inputBackground,
                  color: theme.text,
                  borderColor: theme.border,
                  borderWidth: 1,
                  borderRadius: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  fontSize: 16,
                  minHeight: 80,
                  textAlignVertical: 'top',
                  marginBottom: 24,
                }}
                value={description}
                onChangeText={setDescription}
                placeholder="Enter room description"
                placeholderTextColor={theme.secondaryText}
                multiline
                numberOfLines={4}
                maxLength={200}
              />

              {/* Add Room Button */}
              <ThemedButton
                title={loading ? 'Adding...' : 'Add Room'}
                onPress={handleAddRoom}
                disabled={loading}
                style={{ marginTop: 4 }}
                accessibilityLabel="Add Room"
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    marginBottom: 30,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 0,
  },
  backButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    padding: 20,
  },
  form: {
    width: '100%',
    maxWidth: 600,
    marginHorizontal: 'auto',
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
    marginTop: 0,
  },
  input: {
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    marginBottom: 16,
    height: 44,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  addButtonContainer: {
    width: '100%',
    alignItems: 'center',
    marginTop: 32,
    paddingHorizontal: 20,
  },
  addButton: {
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
    height: 44,
  },
  disabledButton: {
    opacity: 0.5,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
}); 